// Service for managing calendar sharing between users
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, or, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const SHARES_COLLECTION = 'shares';

// Create a new share invitation
export const createShare = async (ownerUserId, ownerEmail, sharedWithEmail, permissions = 'full') => {
  try {
    const sharesRef = collection(db, SHARES_COLLECTION);
    
    // Check if share already exists
    const q = query(
      sharesRef,
      where("ownerUserId", "==", ownerUserId),
      where("sharedWithEmail", "==", sharedWithEmail)
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      console.log(`Share already exists for ${sharedWithEmail}`);
      return { error: 'Share already exists for this email' };
    }
    
    // Add new share
    const docRef = await addDoc(sharesRef, {
      ownerUserId,
      ownerEmail,
      sharedWithEmail,
      sharedWithUserId: null, // Will be filled when recipient accepts
      permissions, // 'full' or 'view' 
      status: 'pending', // 'pending', 'accepted', 'declined'
      createdAt: new Date(),
      acceptedAt: null
    });
    
    console.log(`Share created with ID: ${docRef.id}`);
    return { id: docRef.id, success: true };
  } catch (error) {
    console.error('Error creating share:', error);
    throw error;
  }
};

// Get all shares for a user (both as owner and as recipient)
export const getUserShares = async (userId, userEmail) => {
  try {
    const sharesRef = collection(db, SHARES_COLLECTION);
    
    // Query for shares where user is owner or recipient
    const q = query(
      sharesRef,
      or(
        where("ownerUserId", "==", userId),
        where("sharedWithUserId", "==", userId),
        where("sharedWithEmail", "==", userEmail)
      )
    );
    
    const querySnapshot = await getDocs(q);
    const shares = [];
    
    querySnapshot.forEach((doc) => {
      shares.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return shares;
  } catch (error) {
    console.error('Error getting user shares:', error);
    throw error;
  }
};

// Accept a share invitation
export const acceptShare = async (shareId, userId) => {
  try {
    const shareRef = doc(db, SHARES_COLLECTION, shareId);
    
    // First, let's check the current state of the share
    const shareDoc = await getDoc(shareRef);
    if (!shareDoc.exists()) {
      throw new Error('Share document not found');
    }
    
    const shareData = shareDoc.data();
    console.log('Current share data:', shareData);
    console.log('Attempting to accept with userId:', userId);
    
    // Update the share status
    await updateDoc(shareRef, {
      sharedWithUserId: userId,
      status: 'accepted',
      acceptedAt: new Date()
    });
    
    console.log(`Share ${shareId} accepted by user ${userId}`);
    return { success: true, shareData };
  } catch (error) {
    console.error('Error accepting share:', error);
    console.error('Error details:', error.message, error.code);
    throw error;
  }
};

// Decline a share invitation
export const declineShare = async (shareId) => {
  try {
    const shareRef = doc(db, SHARES_COLLECTION, shareId);
    
    await updateDoc(shareRef, {
      status: 'declined'
    });
    
    console.log(`Share ${shareId} declined`);
    return { success: true };
  } catch (error) {
    console.error('Error declining share:', error);
    throw error;
  }
};

// Remove a share (owner can remove shared access)
export const removeShare = async (shareId) => {
  try {
    const shareRef = doc(db, SHARES_COLLECTION, shareId);
    await deleteDoc(shareRef);
    
    console.log(`Share ${shareId} removed`);
    return { success: true };
  } catch (error) {
    console.error('Error removing share:', error);
    throw error;
  }
};

// Get all users who have access to a calendar (including owner)
export const getCalendarUsers = async (userId) => {
  try {
    const sharesRef = collection(db, SHARES_COLLECTION);
    
    // Get all accepted shares where user is owner
    const ownerQuery = query(
      sharesRef,
      where("ownerUserId", "==", userId),
      where("status", "==", "accepted")
    );
    
    // Get all accepted shares where user is recipient
    const recipientQuery = query(
      sharesRef,
      where("sharedWithUserId", "==", userId),
      where("status", "==", "accepted")
    );
    
    const [ownerSnapshot, recipientSnapshot] = await Promise.all([
      getDocs(ownerQuery),
      getDocs(recipientQuery)
    ]);
    
    const users = [];
    
    // Add shared users from owner's perspective
    ownerSnapshot.forEach((doc) => {
      const data = doc.data();
      users.push({
        userId: data.sharedWithUserId,
        email: data.sharedWithEmail,
        permissions: data.permissions,
        role: 'shared',
        shareId: doc.id
      });
    });
    
    // Add owners from recipient's perspective
    recipientSnapshot.forEach((doc) => {
      const data = doc.data();
      users.push({
        userId: data.ownerUserId,
        email: data.ownerEmail,
        permissions: 'full', // Owner always has full permissions
        role: 'owner',
        shareId: doc.id
      });
    });
    
    // Add self as owner
    users.push({
      userId: userId,
      email: null, // Will need to get from user profile
      permissions: 'full',
      role: 'owner',
      shareId: null
    });
    
    return users;
  } catch (error) {
    console.error('Error getting calendar users:', error);
    throw error;
  }
};

// Subscribe to share updates
export const subscribeToShares = (userId, userEmail, callback) => {
  const sharesRef = collection(db, SHARES_COLLECTION);
  
  // Query for shares involving this user
  const q = query(
    sharesRef,
    or(
      where("ownerUserId", "==", userId),
      where("sharedWithUserId", "==", userId),
      where("sharedWithEmail", "==", userEmail)
    )
  );
  
  // Subscribe to changes
  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const shares = [];
    
    querySnapshot.forEach((doc) => {
      shares.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    callback(shares);
  });
  
  return unsubscribe;
};